import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getGuardianAttackTiming } from './guardianCamera';
import { playSound } from '../../utils/utils';
import { combatHaptic } from '../../utils/combatHaptics';

// The intro shares the guardian scene and attack clock, while its reward
// transaction still resolves once, after the projection has shattered.
const useBreachStrike = ({
  enabled,
  game,
  effectiveRoll,
  reduceMotion,
  onResolve,
}) => {
  const [presentation, setPresentation] = useState(null);
  const key = `breach:${game.roll}:${game.landingIndex}:${game.flipped}`;
  const resolveRef = useRef(onResolve);
  resolveRef.current = onResolve;
  const finishedRef = useRef(null);
  const timing = useMemo(
    () => getGuardianAttackTiming(reduceMotion, { enemyHpAfter: 0 }),
    [reduceMotion]
  );
  const combat =
    enabled && presentation?.combatId === key ? presentation : null;
  const startTime = combat?.startTime;
  const start = () => {
    const damage = Math.max(0, effectiveRoll || 0);
    setPresentation((current) =>
      current?.combatId === key
        ? current
        : {
            combatId: key,
            startTime: 0,
            stage: 'launch',
            ...timing,
            effectiveRoll: damage,
            attackDealt: damage,
            enemyHpBefore: Math.max(1, damage),
            enemyHpAfter: 0,
            playerHpAfter: game.hp,
            retaliation: false,
            boardLanding: game.doorBoard?.[game.landingIndex],
          }
    );
  };
  const onCombatReady = useCallback(() => {
    setPresentation((current) =>
      current?.combatId === key && !current.startTime
        ? { ...current, startTime: Date.now() }
        : current
    );
  }, [key]);

  useEffect(() => {
    if (!enabled || !startTime || finishedRef.current === key) return undefined;
    const timers = [
      setTimeout(
        () =>
          playSound('/sounds/hero-skill-attack.mp3', { preventOverlap: true }),
        timing.launchAt
      ),
      setTimeout(() => {
        setPresentation((current) =>
          current?.combatId === key ? { ...current, stage: 'hit' } : current
        );
        playSound('/sounds/rock-smash.mp3');
        if (effectiveRoll > 0) combatHaptic('dealt');
        playSound('/sounds/instant-explosion-attack.mp3');
      }, timing.hitAt),
      setTimeout(() => {
        if (finishedRef.current === key) return;
        finishedRef.current = key;
        resolveRef.current?.();
      }, timing.finishAt),
    ];
    return () => timers.forEach(clearTimeout);
  }, [enabled, startTime, key, timing, effectiveRoll]);
  return { combat, start, onCombatReady };
};

export default useBreachStrike;
